<template>
  <v-dialog
    v-model="openDetails"
    @click:outside="$vuetify.breakpoint.xsOnly ? null : closeDetails()"
    :fullscreen="$vuetify.breakpoint.xsOnly"
    :persistent="$vuetify.breakpoint.xsOnly"
    max-width="600"
  >
    <v-card solid class="padding-on-children" :class="{'xs': $vuetify.breakpoint.xsOnly, 'has-ticket': (currentTab == 'DETAILS' && (ticketFileId || refundTicketFileId)) }">
      <toolbar
        :onModal="true"
        v-if="$vuetify.breakpoint.xsOnly"
        :emitGoBack="true"
        @goBack="closeDetails"
      />
      <div style="position: sticky; top: 36px; z-index: 100;">
        <v-footer
          class="pa-0 position-relative"
          v-if="$vuetify.breakpoint.smAndUp"
          color="transparent">
          <refresh-button
            :customStyle="{ 'align-self': 'flex-end', 'position': 'absolute', 'top': $vuetify.breakpoint.smAndUp ? '0' : '30px', 'right': '30px', 'z-index': '1' }"
            @refresh="reload()"
          />
        </v-footer>
      </div>
      <v-container class="d-flex flex-column">
        <v-layout v-if="loading" class="pa-4">
          <v-row
            class="fill-height"
            align="center"
            justify="center"
          >
            <v-progress-circular indeterminate color="primary"></v-progress-circular>
          </v-row>
        </v-layout>
        <template v-else-if="item">
          <reservation-data-card
            :image="item.webappPage.coverImageUrl"
            :room="room"
            :date="date"
            :time="time"
            :attendees="attendeeCount"
            :title="webappPageTitle"
            class="my-0"
            :invertIconColor="true"
          >
            <template v-slot:buttons v-if="item.reservation.status === activityReservationStatus.CANCELLED_BY_ACTIVITY_MODIFICATION || (statusNotCompleted && editionAvailable)">
              <v-btn 
                v-if="item.reservation.status === activityReservationStatus.CANCELLED_BY_ACTIVITY_MODIFICATION"
                outlined 
                small
                color="primary lighten-1"
                block
                @click="goToCurrentReservation"
              >
                {{ $t(`activity_reservation.actions.go_to_updated.${reservationMethod.type}`) }}
              </v-btn>
              <v-flex>
                <v-tooltip 
                  v-if="statusNotCompleted && editionAvailable" max-width="300"
                  bottom
                  :disabled="!reservationTimeHasPassed"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <div v-bind="attrs" v-on="on">
                      <v-btn 
                        :disabled="reservationTimeHasPassed" 
                        block
                        color="primary lighten-1"
                        @click="triggerEditionModalOpen = new Date()"
                      >
                        <span class="px-2">{{ $t('activity_reservation.actions.manage') }}</span>
                      </v-btn>
                    </div>
                  </template>
                  {{$t(`activity_reservation.edit.manage_past_limit.${reservationMethod.type}`)}}
                </v-tooltip>
              </v-flex>
            </template>
            <template v-slot:append v-if="createdByModification">
              <div class="created-by-modification__notice">
                {{ $t(`activity_reservation.created_by_modification_notice.${item.reservation.type}`) }}
                
              </div>
              <v-layout justify-end mt-2>
                <v-btn
                  small
                  rounded
                  class="light-background primary--text text--lighten-1"
                  @click="goToPreviousReservation()"
                >
                  {{ $t(`reservations.go_to_previous.${item.reservation.type}`) }}
                  <v-icon x-small right class="primary--text text--lighten-1">mdi-arrow-right</v-icon>
                </v-btn>
              </v-layout>
            </template>
          </reservation-data-card>
          <v-tabs v-model="currentTab" fixed-tabs class="rounded tabs-margin" color="transparent">
            <v-tab
              v-for="tab in tabs"
              :href="'#' + tab"
              :key="tab"
            >
              {{ $t('reservations.details_tabs.' + tab) }}
            </v-tab>
          </v-tabs>
          <v-tabs-items v-model="currentTab">

            <v-tab-item
              value="TIMELINE"
            >
              <v-card flat>
                <reservation-timeline v-if="item" :show="true" :item="item" @goToReservation="goToReservation" @createdByModification="createdByModification = true"/>
              </v-card>
            </v-tab-item>
            <v-tab-item
              value="DETAILS"
            >
              <v-card flat>
                <v-card-text class="remove-margin-from-last">
                  <reservation-order-details v-if="order" class="mt-2 mb-4" :item="item"/>
                  <display-field :label="$t('fields.comments')" icon="3p" :content="item.reservation.comments" />
                  <v-layout v-if="attendeeCount" class="colored-icons ma-0" row wrap>
                    <v-icon small color="primary lighten-1" class="material-symbols-rounded">group</v-icon>
                    <div class="attendees_badge">{{ attendeeCount }}</div>
                    <v-flex xs12 class="mt-2">
                      <v-chip class="attendee-chip" v-for="(attendee, i) in attendees" :key="i">
                        <v-avatar left v-if="attendee.quantity" class="primary lighten-1 white--text">{{ attendee.quantity }}</v-avatar>
                        {{ attendee.type || attendee }}
                      </v-chip>
                    </v-flex>
                  </v-layout>
                </v-card-text>
              </v-card>
            </v-tab-item>
          </v-tabs-items>
        </template>
        <edit-reservation-modal v-if="item" :item="item" :triggerOpen="triggerEditionModalOpen" @reload="reload" />
        <refresh-button
          fixed
          v-if="$vuetify.breakpoint.xsOnly"
          :customStyle="{ 'align-self': 'flex-end', 'z-index': '4', 'right': '15px','bottom': currentTab === 'DETAILS' ? '60px' : '' }"
          @refresh="reload()"
        />
      </v-container>
      <v-footer
        class="flex-button-container ticket-button-container"
        v-if="currentTab == 'DETAILS' && (ticketFileId || refundTicketFileId)"
        :app="$vuetify.breakpoint.xsOnly"
        color="white">
        <v-layout>
          <v-flex v-if="ticketFileId">
            <v-btn @click="openTicket(ticketFileId)" color="primary lighten-1" block>{{$t('reservations.ticket')}}</v-btn>
          </v-flex>
          <v-flex v-if="refundTicketFileId">
            <v-btn @click="openTicket(refundTicketFileId)" color="primary lighten-1" block>{{$t('reservations.refund_ticket')}}</v-btn>
          </v-flex>
        </v-layout>
      </v-footer>
    </v-card>
  </v-dialog>
</template>

<script>
import { reservationTypes, activityReservationStatus } from '@/config'
import ReservationMixins from '@/mixins/Reservation'
import moment from 'moment'
import { ActivityReservationsRequests } from '@/services/activityReservations.requests'
import { displayFormatDate } from '@/lib/helpers/DateUtils'
import { getTimeFormat } from '@/lib/helpers/HoursFormatter'
import { getGuestFullName } from '@/lib/helpers/GuestName'
import { getTextValue } from '@/lib/helpers/LocalizedText'


const activityReservationsService = new ActivityReservationsRequests()

export default {
  data () {
    return {
      moment,
      reservationTypes,
      item: null,
      displayOrder: false,
      loading: false,
      activityReservationStatus,
      triggerEditionModalOpen: null,
      tabs: [
        'TIMELINE',
        'DETAILS',
      ],
      currentTab: 'TIMELINE',
      createdByModification: false,
      drawer: false,
    }
  },
  mixins: [
    ReservationMixins,
  ],
  components: {
    ReservationOrderDetails: () => import('@/components/view-components/reservations/ReservationOrderDetails.vue'),
    ReservationTypeChip: () => import('@/components/view-components/reservations/ReservationTypeChip.vue'),
    Toolbar: () => import('@/components/app-components/Toolbar.vue'),
    ReservationDataCard: () => import('@/components/view-components/reservations/ReservationDataCard.vue'),
    EditReservationModal: () => import('@/components/view-components/reservations/EditReservationModal.vue'),
    ReservationTimeline: () => import('@/components/view-components/reservations/ReservationTimeline.vue'),
    DisplayField: () => import('@/components/app-components/DisplayField.vue'),
    RefreshButton: () => import('@/components/app-components/RefreshButton.vue'),
  },
  computed: {
    reservationMethod () {
      if (!this.item) {
        return null
      }
      const rm = this.item.reservationMethods.find((rM) => {
        return rM.activityReservationMethod.type === this.item.reservation.type
      })
      return rm ? rm.activityReservationMethod : null
    },
    statusNotCompleted () {
      return this.item.reservation.status === activityReservationStatus.ACCEPTED_BY_ACTIVITY_MODIFICATION || 
        this.item.reservation.status === activityReservationStatus.ACCEPTED || 
        this.item.reservation.status === activityReservationStatus.PENDING || 
        this.item.reservation.status === activityReservationStatus.PENDING_MODIFICATION
    },
    editionAvailable () {
      return this.reservationMethod && (this.reservationMethod.minimumModificationTime !== null || this.reservationMethod.minimumCancellationTime !== null)
    },
    reservationTimeHasPassed () {
      return moment().isAfter(moment(this.item.reservation.acceptedDate || this.item.reservation.requestedDate).add(this.item.reservation.acceptedTime || this.item.reservation.requestedTime, 'ms'))
    },
    room () {
      return this.item && this.item.room ? this.item.room.name : null
    },
    date () {
      if (!this.item) {
        return null
      }
      const date = this.item.reservation.acceptedDate || this.item.reservation.requestedDate
      return displayFormatDate(date, 'sm', true)
    },
    time () {
      if (!this.item) {
        return null
      }
      const time = this.item.reservation.acceptedTime || this.item.reservation.requestedTime
      return moment.utc(time).format(getTimeFormat())
    },
    attendeeCount () {
      if (!this.item) {
        return null
      }
      const numeralAttendees = this.item.numeralAttendees.reduce((total, attendee) => {
        return total + attendee.quantity
      }, 0)
      const nominalAttendees = this.item.nominalAttendees.length
      return numeralAttendees || nominalAttendees
    },
    attendees () {
      if (!this.item) {
        return []
      }
      let attendeeArray = null
      if (this.item.numeralAttendees.length) {
        attendeeArray = this.item.numeralAttendees.map((attendee) => {
          return {
            quantity: attendee.quantity,
            type: attendee.attendeeTypeName,
          }
        })
      } else if (this.item.nominalAttendees.length) {
        attendeeArray = this.item.nominalAttendees.map((attendee) => {
          return getGuestFullName(attendee.guest)
        })
      }
      return attendeeArray
    },
    ticketFileId () {
      if (!this.item) {
        return null
      }
      return this.item.reservation.ticketFileId
    },
    refundTicketFileId () {
      if (!this.item) {
        return null
      }
      return this.item.reservation.refundTicketFileId
    },
    order () {
      if (!this.item) {
        return null
      }
      return this.item.order || this.item.pendingOrder
    },
    webappPageTitle () {
      return getTextValue(this.item.webappPage.pageSpecific.title)
    },
  },
  methods: {
    closeDetails () {
      this.item = null
      this.$emit('closeDetails')
    },
    reload () {
      this.$emit('reload')
      this.loading = true
      activityReservationsService
        .getReservation(this.reservationId)
        .then(async (response) => {
          this.item = response
        }).finally(() => {
          this.loading = false
        })
    },
    goToReservation (id) {
      this.$emit('goToReservation', id)
    },
    async goToCurrentReservation () {
      activityReservationsService.getHistory(this.item.reservation.id)
        .then((data) => {
          const currentId = data.find(({ current }) => 
            !!current && current.status === activityReservationStatus.CANCELLED_BY_ACTIVITY_MODIFICATION
          ).current.related_activity_reservation_id

          this.goToReservation (currentId)
        })
    },
    async goToPreviousReservation () {
      activityReservationsService.getHistory(this.item.reservation.id)
        .then((data) => {
          const previousId = data.find(({ previous }) => 
            !!previous && previous.status === activityReservationStatus.CANCELLED_BY_ACTIVITY_MODIFICATION
          ).previous.related_activity_reservation_id

          this.goToReservation (previousId)
        })
    },
    openTicket (fileId) {
      // FIXME: usar enlace como href en lugar de abrir programáticamente
      activityReservationsService.getTicket(this.item.reservation.id, fileId)
        .then(route => {
          window.open(route)
        })
        .catch(() => {})
    },
  },
  watch: {
    openDetails: {
      immediate: true,
      handler () {
        if (this.openDetails) {
          return new Promise(() => {
            activityReservationsService
              .getReservation(this.reservationId)
              .then(async (response) => {
                this.item = response
              })
          })
        } else {
          this.displayOrder = false
        }
      },
    },
  },
  props: {
    openDetails: Boolean,
    reservationId: String,
  },
}
</script>

<style lang="scss">
.ticket-button-container {
  box-shadow: 0px -10px 15px 0px rgba(0, 0, 0, 0.05) !important;
}
.remove-margin-from-last {
  >*:last-child {
    margin-bottom: 0 !important;
  }
}
.created-by-modification__notice {
  color: #1A1D44;
  font-family: 'Mukta Mahee';
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px; /* 142.857% */
  letter-spacing: 0.25px;
}
.padding-on-children {
  padding: 0;
  &.xs:not(.has-ticket) {
    padding-bottom: 50px !important;
  }
  &.xs.has-ticket {
    padding-bottom: 98px !important;
  }
  .container {
    padding: 0;
  }
  .dashboard-item {
    margin-top: 18px !important;
    margin-left: 14px;
    margin-right: 14px;
  }
  .v-tabs {
    margin-left: 12px;
    margin-right: 12px;
    width: calc(100% - 24px);
  }
  .v-card__text {
    padding-left: 24px;
    padding-right: 24px;
  }
}
.attendee-chip {
  height: 23px !important;
  color: var(--v-primary-darken1) !important;
  font-family: 'Mukta Mahee';
  font-size: 13px !important;
  font-style: normal;
  font-weight: 500;
  line-height: 20px;
  letter-spacing: 0.25px;
  background: #D8D9E1 !important;
  margin-bottom: 6px;
  .v-avatar {
    font-family: 'Mukta Mahee';
    height: 21px !important;
    width: 21px !important;
    min-width: 21px !important;
    margin-left: -11px !important;
  }
  &:not(:last-child) {
    margin-right: 4px;
  }
}
.tabs-margin {
  margin-top: 25px;
  padding-left: 25px;
  padding-right: 25px;
}
.rounded {
  flex: none;
  .v-tabs-bar {
    border-radius: 10px;
    border: 1px solid var(--v-grey-2-base);
    height: 33px;
  }
  .v-tab {
    font-family: 'Mukta Mahee';
    font-size: 15px;
    font-style: normal;
    font-weight: 500;
    letter-spacing: 0.25px;
    text-transform: none;
    color: var(--v-grey-2-base) !important;
    &.v-tab--active {
      border-radius: 10px;
      color: white !important;
      background: var(--v-primary-lighten1);
    }
  }
}
</style>
